/**
 * Run-simulation launcher for the dashboard "Run simulation" item.
 *
 * Flow:
 *   1. Open config modal, collect grid + steps + thermal toggle + display name.
 *   2. POST the config to the V4 sim_server (base URL from <meta name=
 *      "simulation-api-base">, JWT from window.Auth.getToken()).
 *   3. Open the progress modal, poll the V4 status endpoint, redraw
 *      the progress bar + status line on each tick.
 *   4. When status==='done', close the modal and trigger dashboard's
 *      case-list refresh — the upload-folder pipeline (see V5 backend
 *      tests) has already inserted a user_files row, so the new sim
 *      shows up alongside any other uploaded case.
 *
 * No production server-side coandaview changes required.  This module
 * uses ONLY the existing /api/upload-folder pipeline on the backend.
 */
(function () {
  'use strict';

  const META = document.querySelector('meta[name="simulation-api-base"]');
  const API_BASE = (META && META.content) || 'http://localhost:8765';
  // V9.5: the V8 live-stream server runs on its own port (default
  // 8766) inside the cccardio container.  Production (Vast.ai +
  // vast_dispatch) will override this per running sim via the meta
  // tag below at template-render time.
  const STREAM_META =
    document.querySelector('meta[name="simulation-stream-base"]');
  const STREAM_BASE = (STREAM_META && STREAM_META.content)
                        || 'http://localhost:8766';
  const POLL_INTERVAL_MS = 800;

  function $(id) {
    return document.getElementById(id);
  }

  function getToken() {
    if (window.Auth && typeof window.Auth.getToken === 'function') {
      return window.Auth.getToken();
    }
    return localStorage.getItem('coandacloud_token') || '';
  }

  function showModal(id) {
    const m = $(id);
    if (m) {
      requestAnimationFrame(() => m.classList.add('active'));
    }
  }

  function hideModal(id) {
    const m = $(id);
    if (m) m.classList.remove('active');
  }

  function setProgress(percent) {
    const bar = $('simProgressBar');
    if (bar) bar.style.width = Math.max(0, Math.min(100, percent)) + '%';
  }

  function setStatusLine(text) {
    const el = $('simProgressStatus');
    if (el) el.textContent = text;
  }

  function setMetaLine(text) {
    const el = $('simProgressMeta');
    if (el) el.textContent = text;
  }

  function showError(message) {
    const el = $('simProgressError');
    if (!el) return;
    el.textContent = message;
    el.style.display = 'block';
    const closeBtn = $('btnCloseSimProgress');
    if (closeBtn) closeBtn.style.display = '';
    const titleEl = $('simProgressTitle');
    if (titleEl) titleEl.textContent = 'Simulation failed';
  }

  function showLiveLink(simId) {
    const link = $('btnOpenLiveView');
    if (!link) return;
    const qs = new URLSearchParams({
      streamBase: STREAM_BASE,
      simId: simId,
    }).toString();
    link.href = '/live-viewer.html?' + qs;
    link.style.display = '';
  }

  function hideLiveLink() {
    const link = $('btnOpenLiveView');
    if (link) link.style.display = 'none';
  }

  function collectConfig() {
    const nx = parseInt($('simNx').value, 10) || 32;
    const ny = parseInt($('simNy').value, 10) || 32;
    const nz = parseInt($('simNz').value, 10) || 32;
    const nSteps = parseInt($('simNSteps').value, 10) || 200;
    const saveEvery = parseInt($('simSaveEvery').value, 10) || 20;
    const enableThermal = $('simEnableThermal').checked;
    const displayName = ($('simDisplayName').value || '').trim()
      || `Simulation ${nx}^3 / ${nSteps} steps`;

    const cfg = {
      nx, ny, nz,
      n_steps: nSteps,
      save_every: saveEvery,
      output_dtype: 'f32',
      compress: null,
      display_name: displayName,
      enable_thermal: enableThermal,
    };
    if (enableThermal) {
      cfg.thermal_alpha = 5e-3;
      cfg.thermal_init_blob = {
        cx: nx / 2,
        cy: ny / 2,
        cz: nz / 2,
        sigma: Math.max(2.0, Math.min(nx, ny, nz) / 8),
        amplitude: 1.0,
      };
    }
    return cfg;
  }

  async function launchSimulation(cfg) {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated — please log in again.');
    }
    const res = await fetch(`${API_BASE}/api/sim/launch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(cfg),
    });
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const j = await res.json();
        if (j && j.detail) detail += ` — ${j.detail}`;
      } catch (_e) { /* non-JSON */ }
      throw new Error(`Launch failed: ${detail}`);
    }
    return res.json();
  }

  async function fetchStatus(simId) {
    const res = await fetch(`${API_BASE}/api/sim/${simId}`);
    if (!res.ok) {
      throw new Error(`Status fetch failed: ${res.status}`);
    }
    return res.json();
  }

  function refreshDashboardCases() {
    // dashboard.js exposes a few candidate refreshers; try them in
    // order so this module stays decoupled from internal API churn.
    if (window.dashboardInstance) {
      const di = window.dashboardInstance;
      if (typeof di.loadCases === 'function') return di.loadCases();
      if (typeof di.loadSimulations === 'function') return di.loadSimulations();
      if (typeof di.refresh === 'function') return di.refresh();
    }
    if (typeof window.loadSimulations === 'function') return window.loadSimulations();
    if (typeof window.refreshCaseList === 'function') return window.refreshCaseList();
    // Fallback: reload page.  Disruptive but guarantees visibility.
    window.location.reload();
  }

  async function pollUntilDone(simId) {
    setStatusLine('Starting…');
    setProgress(2);
    while (true) {
      let j;
      try {
        j = await fetchStatus(simId);
      } catch (e) {
        setStatusLine(`Status fetch failed: ${e.message}`);
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS * 2));
        continue;
      }
      const status = j.status || 'unknown';
      const step = j.step ?? 0;
      const total = j.total_steps ?? 1;
      const eta = j.eta_seconds;
      const wall = j.wall_seconds;

      // Status line.
      if (status === 'running') {
        setStatusLine(`Running — step ${step} / ${total}`);
        const pct = Math.max(2, Math.min(95, (step / total) * 95));
        setProgress(pct);
        // V9.5: surface the "Open live viewer" button as soon as the
        // sim is actually producing frames.  Hidden again once the
        // sim finishes (the stream endpoint won't have a renderer
        // anymore once the V4 launcher subprocess exits).
        showLiveLink(simId);
      } else if (status === 'uploading') {
        setStatusLine('Uploading result…');
        setProgress(96);
        showLiveLink(simId);
      } else if (status === 'starting' || status === 'initializing_taichi') {
        setStatusLine('Initializing GPU…');
      } else if (status === 'done') {
        setStatusLine('Done.');
        setProgress(100);
        hideLiveLink();
      } else if (status === 'failed') {
        setStatusLine('Failed.');
        const err = (j.error && (j.error.message || j.error.exception))
                      || 'Simulation exited with non-zero status.';
        showError(err);
      } else {
        setStatusLine(`Status: ${status}`);
      }

      // Meta line.
      const metaBits = [];
      if (typeof wall === 'number') metaBits.push(`elapsed ${wall.toFixed(1)}s`);
      if (typeof eta === 'number') metaBits.push(`ETA ${eta.toFixed(0)}s`);
      if (j.upload && j.upload.status) {
        metaBits.push(`upload: ${j.upload.status}`);
        if (j.upload.n_uploaded != null && j.upload.n_expected != null) {
          metaBits.push(`${j.upload.n_uploaded}/${j.upload.n_expected} files`);
        }
      }
      setMetaLine(metaBits.join('   '));

      if (status === 'done') return { ok: true, payload: j };
      if (status === 'failed') return { ok: false, payload: j };
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
    }
  }

  // ── V9.8: localStorage-backed active-sim registry ──────────
  // Per-user (per-browser) — survives page reloads.  Each entry:
  //   { simId, displayName, startedAt, status, step, totalSteps,
  //     eta, upload }
  // Cleared when status reaches 'done' or 'failed'.
  const LS_KEY = 'cccardio_active_sims_v1';

  function getActiveSims() {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    } catch { return []; }
  }
  function setActiveSims(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list)); }
    catch {}
    renderTray();
  }
  function upsertActiveSim(entry) {
    const list = getActiveSims();
    const idx = list.findIndex(s => s.simId === entry.simId);
    if (idx === -1) list.push(entry);
    else list[idx] = { ...list[idx], ...entry };
    setActiveSims(list);
  }
  function removeActiveSim(simId) {
    setActiveSims(getActiveSims().filter(s => s.simId !== simId));
  }

  // ── Tray renderer ──────────────────────────────────────────
  function renderTray() {
    const tray = $('runningSimsTray');
    const list = $('runningSimsList');
    const count = $('runningSimsCount');
    if (!tray || !list) return;
    const sims = getActiveSims();
    if (sims.length === 0) {
      tray.style.display = 'none';
      return;
    }
    tray.style.display = '';
    if (count) count.textContent = sims.length;
    list.innerHTML = '';
    sims.forEach(s => {
      const row = document.createElement('div');
      row.style.cssText =
        'display: flex; align-items: center; gap: 12px;' +
        'padding: 10px 12px; background: rgba(0,0,0,0.25);' +
        'border-radius: 8px;';
      const pct = (s.totalSteps && s.totalSteps > 0)
        ? Math.max(2, Math.min(99, (s.step / s.totalSteps) * 100))
        : 5;
      const isStreaming = (s.status === 'running'
                              || s.status === 'uploading');
      row.innerHTML = `
        <div style="flex: 1; min-width: 0;">
          <div style="font-size: 13px; font-weight: 500;
                       white-space: nowrap; overflow: hidden;
                       text-overflow: ellipsis;">${escapeHtml(s.displayName || s.simId)}</div>
          <div style="display: flex; align-items: center; gap: 10px;
                       margin-top: 4px;">
            <div style="flex: 1; height: 4px;
                         background: rgba(255,255,255,0.08);
                         border-radius: 2px; overflow: hidden;">
              <div style="width: ${pct}%; height: 100%;
                           background: linear-gradient(90deg,#3b82f6,#8b5cf6);
                           transition: width 0.3s;"></div>
            </div>
            <span style="font-size: 11px; color: #9ca3af;
                          font-family: 'JetBrains Mono', monospace;
                          white-space: nowrap;">${escapeHtml(s.status || '—')}${
              typeof s.eta === 'number'
                ? ' · ETA ' + s.eta.toFixed(0) + 's' : ''}</span>
          </div>
        </div>
        <button class="tray-btn-progress" data-sim="${s.simId}"
                style="padding: 6px 10px; font-size: 12px;
                       background: rgba(255,255,255,0.06);
                       color: #ddd; border: none; border-radius: 5px;
                       cursor: pointer;">Progress</button>
        <a class="tray-btn-live" data-sim="${s.simId}"
           href="#" target="_blank" rel="noopener"
           style="display: ${isStreaming ? 'inline-block' : 'none'};
                  padding: 6px 10px; font-size: 12px;
                  background: linear-gradient(90deg,#3b82f6,#8b5cf6);
                  color: #fff; border-radius: 5px;
                  text-decoration: none;">Live</a>
      `;
      list.appendChild(row);
    });
    // Wire dynamically-created buttons.
    list.querySelectorAll('.tray-btn-progress').forEach(b => {
      b.addEventListener('click', () => {
        const simId = b.dataset.sim;
        // Switch the existing progress modal to track this sim.
        attachProgressModalToSim(simId);
        showModal('simulationProgressModal');
      });
    });
    list.querySelectorAll('.tray-btn-live').forEach(a => {
      a.addEventListener('click', e => {
        const simId = a.dataset.sim;
        const qs = new URLSearchParams({
          streamBase: STREAM_BASE, simId,
        }).toString();
        a.href = '/live-viewer.html?' + qs;
      });
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g,
      c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;',
                '"':'&quot;', "'":'&#39;' })[c]);
  }

  // ── Background poller per active sim ───────────────────────
  const pollers = new Map();  // simId → AbortController

  function startBackgroundPoll(simId) {
    if (pollers.has(simId)) return;
    const ctrl = { stop: false };
    pollers.set(simId, ctrl);
    (async () => {
      while (!ctrl.stop) {
        let j;
        try { j = await fetchStatus(simId); }
        catch { await sleep(POLL_INTERVAL_MS * 2); continue; }
        const status = j.status || 'unknown';
        upsertActiveSim({
          simId,
          status,
          step: j.step,
          totalSteps: j.total_steps,
          eta: j.eta_seconds,
          upload: j.upload,
          // Don't overwrite displayName if already set.
        });
        if (status === 'done' || status === 'failed') {
          if (status === 'done') {
            setTimeout(() => refreshDashboardCases(), 600);
          }
          // Keep the tray entry briefly so users see "done", then
          // drop it.
          setTimeout(() => removeActiveSim(simId), 2500);
          ctrl.stop = true;
          pollers.delete(simId);
          return;
        }
        await sleep(POLL_INTERVAL_MS);
      }
    })();
  }

  function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  // ── Per-sim progress modal switching ───────────────────────
  let currentlyTrackedSim = null;
  function attachProgressModalToSim(simId) {
    currentlyTrackedSim = simId;
    const entry = getActiveSims().find(s => s.simId === simId);
    const titleEl = $('simProgressTitle');
    if (titleEl) titleEl.textContent =
      entry && entry.status === 'done' ? 'Simulation complete'
        : entry && entry.status === 'failed' ? 'Simulation failed'
        : 'Simulation running';
    const errEl = $('simProgressError');
    if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
    const closeBtn = $('btnCloseSimProgress');
    if (closeBtn) closeBtn.style.display = '';
    if (entry) renderProgressModalFromEntry(entry);
    showLiveLink(simId);
  }

  function renderProgressModalFromEntry(s) {
    const status = s.status || 'unknown';
    const step = s.step ?? 0;
    const total = s.totalSteps ?? 1;
    if (status === 'running') {
      setStatusLine(`Running — step ${step} / ${total}`);
      setProgress(Math.max(2, Math.min(95, (step / total) * 95)));
    } else if (status === 'uploading') {
      setStatusLine('Uploading result…');
      setProgress(96);
    } else if (status === 'done') {
      setStatusLine('Done.');
      setProgress(100);
    } else if (status === 'failed') {
      setStatusLine('Failed.');
    } else {
      setStatusLine('Status: ' + status);
    }
    const metaBits = [];
    if (typeof s.eta === 'number') metaBits.push(`ETA ${s.eta.toFixed(0)}s`);
    if (s.upload && s.upload.status) {
      metaBits.push(`upload: ${s.upload.status}`);
    }
    setMetaLine(metaBits.join('   '));
  }

  // Subscribe the open progress modal to localStorage changes so it
  // stays in sync with the background poller for the currently-
  // tracked sim.  Works whether the entry is created by THIS tab or
  // by another tab.
  window.addEventListener('storage', e => {
    if (e.key !== LS_KEY) return;
    renderTray();
    if (!currentlyTrackedSim) return;
    const entry = getActiveSims().find(s => s.simId === currentlyTrackedSim);
    if (entry) renderProgressModalFromEntry(entry);
  });

  async function onSubmitRun() {
    const submitBtn = $('btnSubmitRunSimulation');
    if (submitBtn) submitBtn.disabled = true;
    try {
      const cfg = collectConfig();
      hideModal('runSimulationModal');
      showModal('simulationProgressModal');
      const closeBtn = $('btnCloseSimProgress');
      if (closeBtn) closeBtn.style.display = 'none';
      const titleEl = $('simProgressTitle');
      if (titleEl) titleEl.textContent = 'Simulation running';
      const errEl = $('simProgressError');
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }

      let launchResp;
      try {
        launchResp = await launchSimulation(cfg);
      } catch (e) {
        showError(e.message);
        return;
      }

      const simId = launchResp.sim_id;
      currentlyTrackedSim = simId;
      // Register in the tray immediately.
      upsertActiveSim({
        simId,
        displayName: cfg.display_name,
        startedAt: Date.now(),
        status: 'starting',
      });
      // Kick off the BACKGROUND poller — survives the modal close.
      startBackgroundPoll(simId);
      setMetaLine(`sim_id ${simId.slice(0, 8)}…`);

      // Foreground poll: keeps the OPEN modal updating until the user
      // closes it.  The background poller continues independently.
      const result = await pollUntilDone(simId);
      const closeBtn2 = $('btnCloseSimProgress');
      if (closeBtn2) closeBtn2.style.display = '';
      if (result.ok) {
        const titleEl2 = $('simProgressTitle');
        if (titleEl2) titleEl2.textContent = 'Simulation complete';
        setTimeout(() => refreshDashboardCases(), 600);
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  // ── Admin-only gate (until VA-track caps land) ──────────────
  // Hides the "Run simulation" dropdown item + the running-sims
  // tray for non-admin users.  CCcardio V4 enforces server-side
  // too — see lbm3d/sim_server.py `_require_admin_jwt`.
  function isAdmin() {
    if (!window.Auth || typeof window.Auth.getCurrentUser !== 'function') {
      return false;
    }
    const u = window.Auth.getCurrentUser();
    return !!(u && u.is_admin);
  }

  function init() {
    const btnRun = $('btnRunSimulation');
    if (!btnRun) return; // module loaded on a page without the button
    if (!isAdmin()) {
      // Hide the dropdown item AND the tray — non-admins should
      // see no trace of the simulation features until VA-track
      // billing is in place.
      btnRun.style.display = 'none';
      const tray = $('runningSimsTray');
      if (tray) tray.style.display = 'none';
      // Don't wire any handlers; bail out early.
      return;
    }
    btnRun.addEventListener('click', () => {
      const dropdown = $('newSimulationDropdown');
      if (dropdown) dropdown.classList.remove('open');
      showModal('runSimulationModal');
      const nameInput = $('simDisplayName');
      if (nameInput) setTimeout(() => nameInput.focus(), 50);
    });

    const closeRun = $('btnCloseRunSimulation');
    const cancelRun = $('btnCancelRunSimulation');
    if (closeRun) closeRun.addEventListener('click',
      () => hideModal('runSimulationModal'));
    if (cancelRun) cancelRun.addEventListener('click',
      () => hideModal('runSimulationModal'));
    const submitRun = $('btnSubmitRunSimulation');
    if (submitRun) submitRun.addEventListener('click', onSubmitRun);

    const closeProg = $('btnCloseSimProgress');
    if (closeProg) closeProg.addEventListener('click',
      () => hideModal('simulationProgressModal'));

    // V9.8: tray bootstrap.  On page load we restore any previously-
    // running sims from localStorage and re-arm the background poll
    // (the sim subprocess is still running on the cccardio side).
    // Stale entries (>2h, or status already done/failed beyond the
    // 2.5 s grace) are cleared.
    const STALE_MS = 2 * 60 * 60 * 1000;
    const now = Date.now();
    const fresh = getActiveSims().filter(s => {
      if (s.status === 'done' || s.status === 'failed') return false;
      if (s.startedAt && now - s.startedAt > STALE_MS) return false;
      return true;
    });
    setActiveSims(fresh);
    fresh.forEach(s => startBackgroundPoll(s.simId));
    renderTray();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
